import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronLeft, ChevronRight, Layers, X, Check, Calendar as CalendarIcon, ChevronDown, ChevronUp, Percent } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AdminLayout from "@/components/AdminLayout";
import OccupancyChart from "@/components/OccupancyChart";

const WEEKDAYS_LBL = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTHS_TR = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const VIEW_OPTIONS = [7, 14, 21, 30];

import { toLocalYMD as ymd, parseYMD } from "@/lib/dateUtils";
function addDays(d, n) { const x = new Date(d); x.setHours(0,0,0,0); x.setDate(x.getDate() + n); return x; }
function isoWeekday(d) { return (d.getDay() + 6) % 7; } // 0=Mon..6=Sun

export default function AdminCalendarPage({
    Layout = AdminLayout,
    listRoute = "/admin/hotels",
    skipAuthCheck = false,
}) {
    const { id: hotelId } = useParams();
    const navigate = useNavigate();
    const { user, loading: authLoading } = useAuth() || {};

    const [hotel, setHotel] = useState(null);
    const [plans, setPlans] = useState([]);
    const [entries, setEntries] = useState({}); // key: `${date}|${room_type_id}|${rate_plan_id}` -> entry
    const [loading, setLoading] = useState(false);

    const [startDate, setStartDate] = useState(() => { const d = new Date(); d.setHours(0,0,0,0); return d; });
    const [days, setDays] = useState(14);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [discountOpen, setDiscountOpen] = useState(false);
    const [collapsed, setCollapsed] = useState({}); // room_type_id -> bool

    // Plan filter: if non-null show only these plans (multi)
    const [planFilter, setPlanFilter] = useState([]); // empty array = all

    const dateList = useMemo(() => Array.from({ length: days }, (_, i) => addDays(startDate, i)), [startDate, days]);
    const fromYmd = useMemo(() => ymd(dateList[0]), [dateList]);
    const toYmd = useMemo(() => ymd(dateList[dateList.length - 1]), [dateList]);

    // Group plans by room_type_id (multi-room aware: a plan appears under each of its rooms)
    // Room names come from live hotel.room_types (not stale plan.room_type_name)
    const groupedPlans = useMemo(() => {
        const filtered = planFilter.length ? plans.filter((p) => planFilter.includes(p.id)) : plans;
        const roomNameById = {};
        (hotel?.room_types || []).forEach((rt) => { roomNameById[rt.id] = rt.name; });
        // Fallback: plan-recorded names
        filtered.forEach((p) => {
            if (p.room_type_ids && p.room_type_names) {
                p.room_type_ids.forEach((rid, idx) => {
                    if (!roomNameById[rid]) roomNameById[rid] = p.room_type_names[idx] || p.room_type_name;
                });
            }
            if (p.room_type_id && p.room_type_name && !roomNameById[p.room_type_id]) {
                roomNameById[p.room_type_id] = p.room_type_name;
            }
        });
        const groups = {};
        filtered.forEach((p) => {
            const rtIds = (p.room_type_ids && p.room_type_ids.length) ? p.room_type_ids : (p.room_type_id ? [p.room_type_id] : []);
            rtIds.forEach((rtId) => {
                const rtName = roomNameById[rtId] || "Oda";
                if (!groups[rtId]) groups[rtId] = { room_type_id: rtId, room_type_name: rtName, plans: [] };
                groups[rtId].plans.push(p);
            });
        });
        return Object.values(groups);
    }, [plans, planFilter, hotel]);

    const loadBase = async () => {
        const [h, p] = await Promise.all([
            api.get(`/admin/hotels/${hotelId}`),
            api.get(`/admin/hotels/${hotelId}/rate-plans`),
        ]);
        setHotel(h.data);
        setPlans(p.data);
    };

    const loadCalendar = useCallback(async () => {
        if (!hotelId) return;
        setLoading(true);
        try {
            const r = await api.get(`/admin/hotels/${hotelId}/calendar?from_date=${fromYmd}&to_date=${toYmd}`);
            const map = {};
            (r.data || []).forEach((e) => {
                map[`${e.date}|${e.room_type_id}|${e.rate_plan_id}`] = e;
            });
            setEntries(map);
        } finally {
            setLoading(false);
        }
    }, [hotelId, fromYmd, toYmd]);

    useEffect(() => {
        if (!skipAuthCheck) {
            if (authLoading) return;
            const ALLOWED = ["super_admin", "admin", "otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
            if (!user || !ALLOWED.includes(user.role)) return navigate("/login");
            // Hotel-bound users can only see their own hotel's calendar
            const HOTEL_ROLES = ["otel_owner", "otel_manager", "otel_reception", "otel_accounting"];
            if (HOTEL_ROLES.includes(user.role) && user.hotel_id && user.hotel_id !== hotelId) {
                return navigate(`/admin/hotels/${user.hotel_id}/calendar`, { replace: true });
            }
        }
        loadBase();
    }, [user, authLoading, hotelId, skipAuthCheck]); // eslint-disable-line

    useEffect(() => { loadCalendar(); }, [loadCalendar]);

    const entryFor = (date, rtId, planId) => entries[`${date}|${rtId}|${planId}`];

    const patchCell = async (date, rtId, planId, patch) => {
        // optimistic update
        const key = `${date}|${rtId}|${planId}`;
        const cur = entries[key] || { date, room_type_id: rtId, rate_plan_id: planId };
        setEntries((prev) => ({ ...prev, [key]: { ...cur, ...patch } }));
        try {
            const r = await api.patch(`/admin/hotels/${hotelId}/calendar/cell`, {
                date, room_type_id: rtId, rate_plan_id: planId, ...patch,
            });
            setEntries((prev) => ({ ...prev, [key]: r.data }));
        } catch (e) {
            // revert on error
            setEntries((prev) => ({ ...prev, [key]: cur }));
            alert(e.response?.data?.detail || "Kaydedilemedi");
        }
    };

    const toggleStopSale = (date, rtId, planId) => {
        const cur = entryFor(date, rtId, planId);
        const newVal = !(cur?.stop_sale);
        patchCell(date, rtId, planId, { stop_sale: newVal });
    };

    /* ===== Range selection (shift+click veya mouse-drag) ===== */
    const [anchor, setAnchor] = useState(null); // last click { planId, roomId, fieldType, date }
    const [selection, setSelection] = useState(null); // { planId, roomId, fieldType, start, end }
    const [bulkValue, setBulkValue] = useState("");
    const [bulkSaving, setBulkSaving] = useState(false);

    // Drag-select state (mouse-down + drag instead of Shift+click)
    const isDraggingRef = React.useRef(false);
    const dragAnchorRef = React.useRef(null);
    const wasMultiDragRef = React.useRef(false);

    React.useEffect(() => {
        const onUp = () => {
            isDraggingRef.current = false;
            dragAnchorRef.current = null;
            document.body.classList.remove("select-none");
            // Reset multi-drag flag after click event has fired
            setTimeout(() => { wasMultiDragRef.current = false; }, 50);
        };
        window.addEventListener("mouseup", onUp);
        return () => window.removeEventListener("mouseup", onUp);
    }, []);

    const startDragSelect = (planId, roomId, fieldType, date) => {
        isDraggingRef.current = true;
        dragAnchorRef.current = { planId, roomId, fieldType, date };
        wasMultiDragRef.current = false;
        document.body.classList.add("select-none");
        setAnchor({ planId, roomId, fieldType, date });
        setSelection({ planId, roomId, fieldType, start: date, end: date });
    };

    const dragExtend = (planId, roomId, fieldType, date) => {
        if (!isDraggingRef.current) return;
        const a = dragAnchorRef.current;
        if (!a) return;
        if (a.planId !== planId || a.roomId !== roomId || a.fieldType !== fieldType) return;
        if (a.date !== date) wasMultiDragRef.current = true;
        const [s, e] = [a.date, date].sort();
        setSelection({ planId, roomId, fieldType, start: s, end: e });
    };

    const wasMultiDrag = () => wasMultiDragRef.current;

    const setAnchorOnly = (planId, roomId, fieldType, date) => {
        setAnchor({ planId, roomId, fieldType, date });
        setSelection(null);
    };

    const extendSelection = (planId, roomId, fieldType, date) => {
        if (anchor && anchor.planId === planId && anchor.roomId === roomId && anchor.fieldType === fieldType) {
            const [s, e] = [anchor.date, date].sort();
            setSelection({ planId, roomId, fieldType, start: s, end: e });
        } else {
            setAnchor({ planId, roomId, fieldType, date });
            setSelection({ planId, roomId, fieldType, start: date, end: date });
        }
    };

    const inSelection = (planId, roomId, fieldType, date) => {
        if (!selection) return false;
        if (selection.planId !== planId || selection.roomId !== roomId || selection.fieldType !== fieldType) return false;
        return date >= selection.start && date <= selection.end;
    };

    const selectionCount = useMemo(() => {
        if (!selection) return 0;
        const s = parseYMD(selection.start);
        const e = parseYMD(selection.end);
        return Math.round((e - s) / 86400000) + 1;
    }, [selection]);

    const applyBulkSelection = async (op, value) => {
        if (!selection) return;
        setBulkSaving(true);
        try {
            // Special path: discount field uses dedicated endpoint
            if (selection.fieldType === "discount") {
                const body = {
                    start_date: selection.start,
                    end_date: selection.end,
                    weekdays: [0, 1, 2, 3, 4, 5, 6],
                    room_type_ids: [selection.roomId],
                    rate_plan_ids: [selection.planId],
                    discount_type: op === "discount_clear" ? "clear" : op.replace("discount_", ""), // discount_percent → percent
                    discount_value: op === "discount_clear" ? null : Number(value),
                    discount_label: null,
                    discount_min_nights: null,
                };
                await api.post(`/admin/hotels/${hotelId}/calendar/discount-bulk`, body);
                setSelection(null);
                setBulkValue("");
                await loadCalendar();
                return;
            }
            const body = {
                hotel_id: hotelId,
                start_date: selection.start,
                end_date: selection.end,
                weekdays: [0, 1, 2, 3, 4, 5, 6],
                rate_plan_ids: [selection.planId],
                room_type_ids: [selection.roomId],
                operation: op,
            };
            if (op === "price_only") {
                body[selection.fieldType] = Number(value);
            } else if (op === "quota_only") {
                body.quota = Number(value);
            }
            await api.post(`/admin/hotels/${hotelId}/calendar/bulk`, body);
            setSelection(null);
            setBulkValue("");
            await loadCalendar();
        } catch (e) {
            alert(e.response?.data?.detail || "Toplu güncelleme başarısız");
        } finally {
            setBulkSaving(false);
        }
    };

    const moveDays = (n) => setStartDate((d) => addDays(d, n));
    const today = () => { const d = new Date(); d.setHours(0,0,0,0); setStartDate(d); };

    if (!hotel) return <Layout title="Yükleniyor..."><div>Yükleniyor...</div></Layout>;

    const isNet = (hotel.pricing_type || "commission") === "net";
    const togglePlanFilter = (id) => setPlanFilter((f) => f.includes(id) ? f.filter((x) => x !== id) : [...f, id]);
    const toggleRoomCollapse = (rtId) => setCollapsed((c) => ({ ...c, [rtId]: !c[rtId] }));

    return (
        <Layout
            title={`${hotel.name} - Takvim`}
            action={
                <div className="flex gap-2 flex-wrap items-center">
                    <Link to={`/admin/hotels/${hotelId}/rates`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-semibold whitespace-nowrap">💰 Fiyat Planları</Link>
                    <button onClick={() => navigate(listRoute)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-medium text-slate-700 dark:text-slate-200 whitespace-nowrap">
                        <ArrowLeft className="h-4 w-4" /> Geri
                    </button>
                    <button data-testid="bulk-update-btn" onClick={() => setBulkOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold whitespace-nowrap">
                        <Layers className="h-4 w-4" /> Toplu Güncelle
                    </button>
                    <button data-testid="discount-bulk-btn" onClick={() => setDiscountOpen(true)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-semibold whitespace-nowrap">
                        <Percent className="h-4 w-4" /> İndirim Uygula
                    </button>
                </div>
            }
        >
            <p className="text-sm text-slate-500 mb-3">Tarih bazlı fiyat, kontenjan ve stop-sale yönetimi. <span className="font-semibold text-emerald-600">Yeşil ✓</span> ikonuna tıklayarak <span className="font-semibold text-red-600">kırmızı ✕</span> (satış kapalı) duruma alabilirsiniz. Sayı hücrelerine tıklayıp doğrudan düzenleyebilirsiniz.</p>

            {/* Occupancy chart */}
            <div className="mb-4">
                <OccupancyChart hotelId={hotelId} days={30} />
            </div>

            {plans.length === 0 ? (
                <div className="p-12 text-center rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800">
                    <p className="text-slate-500 mb-4">Bu otelin henüz fiyat planı yok.</p>
                    <Link to={`/admin/hotels/${hotelId}/rates`} className="inline-block px-5 py-2.5 rounded-xl bg-sky-500 text-white font-semibold">Fiyat Planı Ekle</Link>
                </div>
            ) : (
                <>
                    {/* Toolbar */}
                    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 mb-3 flex flex-wrap items-center gap-2">
                        <button onClick={() => moveDays(-days)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="prev-period"><ChevronLeft className="h-4 w-4" /></button>
                        <div className="relative">
                            <input
                                type="date"
                                value={ymd(startDate)}
                                onChange={(e) => setStartDate(parseYMD(e.target.value))}
                                className="px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium"
                                data-testid="date-picker"
                            />
                        </div>
                        <button onClick={today} className="px-3 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-sm font-medium hover:bg-slate-200 dark:hover:bg-slate-700" data-testid="today-btn">Bugün</button>
                        <button onClick={() => moveDays(days)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" data-testid="next-period"><ChevronRight className="h-4 w-4" /></button>

                        <div className="ml-auto flex items-center gap-1">
                            <span className="text-xs font-bold uppercase tracking-widest text-slate-500 mr-1">Görünüm:</span>
                            {VIEW_OPTIONS.map((v) => (
                                <button
                                    key={v}
                                    onClick={() => setDays(v)}
                                    data-testid={`view-${v}d`}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold border ${days === v ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"}`}
                                >{v} gün</button>
                            ))}
                        </div>
                    </div>

                    {/* Plan filter chips */}
                    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-3 mb-3">
                        <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Fiyat Planı Filtresi {planFilter.length > 0 && <span className="text-sky-500">({planFilter.length} seçili)</span>}</div>
                        <div className="flex flex-wrap gap-1.5">
                            <button
                                onClick={() => setPlanFilter([])}
                                className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${planFilter.length === 0 ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"}`}
                                data-testid="plan-filter-all"
                            >Tümü</button>
                            {plans.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => togglePlanFilter(p.id)}
                                    data-testid={`plan-filter-${p.code}`}
                                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${planFilter.includes(p.id) ? "bg-sky-500 text-white border-sky-500" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300"}`}
                                >
                                    {p.code} · {p.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Main grid table */}
                    <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 overflow-hidden">
                        <div className="overflow-auto max-h-[calc(100vh-260px)]" data-testid="calendar-table">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr>
                                        <th className="sticky left-0 top-0 z-30 bg-slate-100 dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700 p-1.5 text-left text-[10px] font-bold uppercase tracking-widest text-slate-500 w-[220px] min-w-[220px]">
                                            Oda / Fiyat Planı
                                        </th>
                                        {dateList.map((d) => {
                                            const wd = isoWeekday(d);
                                            const isWeekend = wd >= 5;
                                            return (
                                                <th key={ymd(d)} className={`sticky top-0 z-20 border-b border-slate-200 dark:border-slate-700 p-1 text-center text-[10px] font-semibold w-[76px] min-w-[76px] ${isWeekend ? "bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-300" : "bg-slate-50 dark:bg-slate-800/50 text-slate-700 dark:text-slate-300"}`}>
                                                    <div className="text-[10px]">{MONTHS_TR[d.getMonth()]} {d.getDate()}</div>
                                                    <div className="text-[9px] uppercase opacity-70">{WEEKDAYS_LBL[wd]}</div>
                                                </th>
                                            );
                                        })}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupedPlans.length === 0 && (
                                        <tr><td colSpan={dateList.length + 1} className="p-6 text-center text-sm text-slate-500">Seçili filtreye uygun plan bulunamadı.</td></tr>
                                    )}
                                    {groupedPlans.map((group) => {
                                        const isCollapsed = collapsed[group.room_type_id];
                                        return (
                                            <React.Fragment key={group.room_type_id}>
                                                {/* Room type header row */}
                                                <tr>
                                                    <td colSpan={dateList.length + 1} className="sticky left-0 bg-sky-50 dark:bg-sky-950/30 border-b border-slate-200 dark:border-slate-700 p-2">
                                                        <button onClick={() => toggleRoomCollapse(group.room_type_id)} className="flex items-center gap-2 text-sm font-bold text-sky-900 dark:text-sky-200" data-testid={`room-toggle-${group.room_type_id}`}>
                                                            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
                                                            {group.room_type_name} <span className="text-xs font-medium text-sky-700 dark:text-sky-400">({group.plans.length} plan)</span>
                                                        </button>
                                                    </td>
                                                </tr>
                                                {!isCollapsed && group.plans.map((p) => {
                                                    const rt = (hotel?.room_types || []).find((r) => r.id === group.room_type_id);
                                                    return (
                                                        <PlanRows
                                                            key={p.id}
                                                            plan={p}
                                                            roomTypeId={group.room_type_id}
                                                            capacity={Math.max(1, Math.min(6, Number(rt?.capacity || 2)))}
                                                            dateList={dateList}
                                                            entryFor={entryFor}
                                                            toggleStopSale={toggleStopSale}
                                                            patchCell={patchCell}
                                                            setAnchorOnly={setAnchorOnly}
                                                            extendSelection={extendSelection}
                                                            startDragSelect={startDragSelect}
                                                            dragExtend={dragExtend}
                                                            wasMultiDrag={wasMultiDrag}
                                                            inSelection={inSelection}
                                                            isNet={isNet}
                                                        />
                                                    );
                                                })}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {loading && <div className="text-xs text-slate-500 mt-2">Yükleniyor...</div>}

                    <div className="mt-3 flex flex-wrap gap-4 text-xs text-slate-500">
                        <span className="inline-flex items-center gap-1"><Check className="h-3 w-3 text-emerald-500" /> Satışa açık</span>
                        <span className="inline-flex items-center gap-1"><X className="h-3 w-3 text-red-500" /> Stop sale</span>
                        <span className="inline-flex items-center gap-1"><span className="px-1 rounded bg-orange-500 text-white text-[9px] font-bold">G</span> Girişe kapalı</span>
                        <span className="inline-flex items-center gap-1"><span className="px-1 rounded bg-violet-500 text-white text-[9px] font-bold">Ç</span> Çıkışa kapalı</span>
                        <span><kbd className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-mono text-[10px]">Sürükle</kbd> → aralık seç (toplu güncelle) <span className="text-slate-400">veya</span> <kbd className="px-1.5 py-0.5 rounded bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 font-mono text-[10px]">Shift</kbd>+tıkla</span>
                    </div>
                </>
            )}

            {/* Range selection floating bar */}
            {selection && (
                <SelectionBar
                    selection={selection}
                    count={selectionCount}
                    plans={plans}
                    bulkValue={bulkValue}
                    setBulkValue={setBulkValue}
                    saving={bulkSaving}
                    onClear={() => { setSelection(null); setBulkValue(""); }}
                    onApply={applyBulkSelection}
                />
            )}

            {bulkOpen && (
                <BulkUpdateDrawer
                    hotel={hotel}
                    plans={plans}
                    hotelId={hotelId}
                    onClose={() => setBulkOpen(false)}
                    onSaved={() => { setBulkOpen(false); loadCalendar(); }}
                />
            )}
            {discountOpen && (
                <DiscountBulkDrawer
                    hotel={hotel}
                    plans={plans}
                    hotelId={hotelId}
                    onClose={() => setDiscountOpen(false)}
                    onSaved={() => { setDiscountOpen(false); loadCalendar(); }}
                />
            )}
        </Layout>
    );
}

/* ====================== DISCOUNT BULK DRAWER ====================== */
function DiscountBulkDrawer({ hotel, plans, hotelId, onClose, onSaved }) {
    const today = ymd(new Date());
    const monthAhead = ymd(addDays(new Date(), 30));
    const roomTypes = hotel?.room_types || [];

    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(monthAhead);
    const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [selectedRoomIds, setSelectedRoomIds] = useState([]); // empty = all
    const [selectedPlanIds, setSelectedPlanIds] = useState([]); // empty = all
    const [discountType, setDiscountType] = useState("percent"); // percent | fixed | override | clear
    const [discountValue, setDiscountValue] = useState("");
    const [discountLabel, setDiscountLabel] = useState("");
    const [minNights, setMinNights] = useState("");
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    const toggleWd = (i) => setWeekdays((p) => p.includes(i) ? p.filter((x) => x !== i) : [...p, i].sort());
    const toggleRoom = (rid) => setSelectedRoomIds((p) => p.includes(rid) ? p.filter((x) => x !== rid) : [...p, rid]);
    const togglePlan = (pid) => setSelectedPlanIds((p) => p.includes(pid) ? p.filter((x) => x !== pid) : [...p, pid]);

    const submit = async () => {
        setMsg("");
        if (!startDate || !endDate) { setMsg("Tarih aralığı gerekli"); return; }
        if (discountType !== "clear" && (!discountValue || Number(discountValue) <= 0)) { setMsg("Geçerli bir indirim değeri girin"); return; }
        setSaving(true);
        try {
            const r = await api.post(`/admin/hotels/${hotelId}/calendar/discount-bulk`, {
                start_date: startDate,
                end_date: endDate,
                weekdays,
                room_type_ids: selectedRoomIds,
                rate_plan_ids: selectedPlanIds,
                discount_type: discountType,
                discount_value: discountType === "clear" ? null : Number(discountValue),
                discount_label: discountLabel,
                discount_min_nights: minNights ? Number(minNights) : null,
            });
            setMsg(`✓ ${r.data.updated} hücre güncellendi`);
            setTimeout(onSaved, 800);
        } catch (e) {
            setMsg(e.response?.data?.detail || "Hata oluştu");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex" onClick={onClose}>
            <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" />
            <div className="relative ml-auto h-full w-full sm:max-w-xl bg-white dark:bg-slate-900 shadow-2xl overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between sticky top-0 bg-white dark:bg-slate-900 z-10">
                    <div className="flex items-center gap-2">
                        <Percent className="h-5 w-5 text-amber-500" />
                        <h2 className="font-display text-base font-bold">İndirim Uygula / Kaldır</h2>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Tarih Aralığı */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Tarih Aralığı</label>
                        <div className="grid grid-cols-2 gap-2">
                            <input data-testid="disc-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                            <input data-testid="disc-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                        </div>
                    </div>

                    {/* Haftanın Günleri */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Haftanın Günleri</label>
                        <div className="flex flex-wrap gap-1.5">
                            {WEEKDAYS_LBL.map((w, i) => (
                                <button key={i} onClick={() => toggleWd(i)} data-testid={`disc-wd-${i}`}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${weekdays.includes(i) ? "bg-amber-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {w}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Oda Tipleri */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Oda Tipleri <span className="text-slate-400 normal-case font-normal">(boş = hepsi)</span></label>
                        <div className="flex flex-wrap gap-1.5">
                            {roomTypes.map((rt) => (
                                <button key={rt.id} onClick={() => toggleRoom(rt.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${selectedRoomIds.includes(rt.id) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {rt.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Pansiyon (Rate Plan) */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Pansiyon (Fiyat Planı) <span className="text-slate-400 normal-case font-normal">(boş = hepsi)</span></label>
                        <div className="flex flex-wrap gap-1.5">
                            {plans.map((p) => (
                                <button key={p.id} onClick={() => togglePlan(p.id)}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${selectedPlanIds.includes(p.id) ? "bg-violet-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {p.name || p.code}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* İndirim Tipi */}
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">İndirim Tipi</label>
                        <div className="grid grid-cols-4 gap-2">
                            {[
                                { v: "percent", l: "Yüzde %" },
                                { v: "fixed", l: "Sabit ₺" },
                                { v: "override", l: "Yeni Fiyat" },
                                { v: "clear", l: "Kaldır" },
                            ].map((opt) => (
                                <button key={opt.v} onClick={() => setDiscountType(opt.v)} data-testid={`disc-type-${opt.v}`}
                                    className={`px-3 py-2 rounded-lg text-xs font-bold transition-colors ${discountType === opt.v ? (opt.v === "clear" ? "bg-red-500 text-white" : "bg-amber-500 text-white") : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {opt.l}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Değer */}
                    {discountType !== "clear" && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                                        {discountType === "percent" ? "Yüzde (%)" : discountType === "fixed" ? "Sabit Tutar (₺)" : "Yeni Fiyat (₺)"}
                                    </label>
                                    <input data-testid="disc-value" type="number" min="0" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" placeholder={discountType === "percent" ? "15" : "150"} />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Min. Kalış (gece)</label>
                                    <input data-testid="disc-min-nights" type="number" min="0" value={minNights} onChange={(e) => setMinNights(e.target.value)}
                                        className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" placeholder="0 = koşulsuz" />
                                </div>
                            </div>

                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Açıklama (opsiyonel)</label>
                                <input data-testid="disc-label" value={discountLabel} onChange={(e) => setDiscountLabel(e.target.value)}
                                    className="w-full px-3 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm"
                                    placeholder='Örn: "Erken Rezervasyon", "Son Dakika"' />
                            </div>
                        </>
                    )}

                    {msg && <div className={`text-xs font-semibold ${msg.startsWith("✓") ? "text-emerald-600" : "text-red-600"}`}>{msg}</div>}

                    <button data-testid="disc-apply-btn" onClick={submit} disabled={saving}
                        className={`w-full py-3 rounded-xl text-sm font-bold text-white shadow-lg transition-all ${discountType === "clear" ? "bg-red-500 hover:bg-red-600 disabled:bg-slate-300" : "bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300"}`}>
                        {saving ? "Uygulanıyor..." : discountType === "clear" ? "İndirimleri Kaldır" : "İndirimi Uygula"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function PlanRows({ plan, roomTypeId, capacity = 2, dateList, entryFor, toggleStopSale, patchCell, setAnchorOnly, extendSelection, startDragSelect, dragExtend, wasMultiDrag, inSelection, isNet }) {
    const sharedProps = { dateList, plan, roomTypeId, entryFor, patchCell, setAnchorOnly, extendSelection, startDragSelect, dragExtend, wasMultiDrag, inSelection };
    const caps = Array.from({ length: capacity }, (_, i) => i + 1);
    const singleCap = capacity <= 1;
    return (
        <>
            <tr>
                <td colSpan={dateList.length + 1} className="sticky left-0 bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700 px-3 py-1">
                    <span className="text-[11px] font-bold text-slate-700 dark:text-slate-200">{plan.code} · {plan.name}</span>
                    <span className="ml-2 text-[9px] uppercase tracking-widest text-slate-500">{plan.board || ""}</span>
                </td>
            </tr>
            <SubRow label="Müsaitlik" fieldType="stop_sale" toggleStopSale={toggleStopSale} {...sharedProps} />
            <SubRow label="Kontenjan" fieldType="quota" {...sharedProps} />
            {isNet && (
                singleCap
                    ? <SubRow label="Maliyet" fieldType="cost_price" {...sharedProps} />
                    : caps.map((k) => (
                        <SubRow key={`cost_${k}`} label={`Maliyet ${k} kişi`} fieldType={`cost_price_${k}`} {...sharedProps} />
                    ))
            )}
            {singleCap
                ? <SubRow label={isNet ? "Satış" : "Fiyat"} fieldType="price" {...sharedProps} />
                : caps.map((k) => (
                    <SubRow key={`price_${k}`} label={`${isNet ? "Satış" : "Fiyat"} ${k} kişi`} fieldType={`price_${k}`} {...sharedProps} />
                ))
            }
            <SubRow label="İndirim" fieldType="discount" {...sharedProps} />
            <SubRow label="Release (gün)" fieldType="release" {...sharedProps} />
        </>
    );
}

function SubRow({ label, dateList, plan, roomTypeId, fieldType, entryFor, toggleStopSale, patchCell, setAnchorOnly, extendSelection, startDragSelect, dragExtend, wasMultiDrag, inSelection }) {
    return (
        <tr>
            <td className="sticky left-0 z-10 bg-white dark:bg-slate-900 border-b border-r border-slate-200 dark:border-slate-700 px-3 py-1 text-[11px] font-medium text-slate-600 dark:text-slate-300">
                <span className="ml-4">{label}</span>
            </td>
            {dateList.map((d) => {
                const ds = ymd(d);
                const entry = entryFor(ds, roomTypeId, plan.id);
                const wd = isoWeekday(d);
                const isWeekend = wd >= 5;
                const selected = inSelection ? inSelection(plan.id, roomTypeId, fieldType, ds) : false;
                const selRing = selected ? "ring-2 ring-sky-500 ring-inset bg-sky-100/60 dark:bg-sky-950/40" : "";
                const cellBase = `border-b border-r border-slate-100 dark:border-slate-800 text-center align-middle ${isWeekend ? "bg-amber-50/30 dark:bg-amber-950/10" : ""} ${selRing}`;
                if (fieldType === "stop_sale") {
                    const isStop = !!entry?.stop_sale;
                    return (
                        <td key={ds} className={`${cellBase} p-0`}>
                            <button
                                onMouseDown={(e) => {
                                    if (e.button !== 0 || e.shiftKey) return;
                                    startDragSelect(plan.id, roomTypeId, fieldType, ds);
                                }}
                                onMouseEnter={() => dragExtend(plan.id, roomTypeId, fieldType, ds)}
                                onClick={(e) => {
                                    if (e.shiftKey) { extendSelection(plan.id, roomTypeId, fieldType, ds); return; }
                                    if (wasMultiDrag && wasMultiDrag()) return; // drag-select, no toggle
                                    setAnchorOnly(plan.id, roomTypeId, fieldType, ds);
                                    toggleStopSale(ds, roomTypeId, plan.id);
                                }}
                                data-testid={`avail-${plan.id}-${ds}`}
                                className={`w-full h-7 inline-flex items-center justify-center transition-colors ${isStop ? "bg-red-100 dark:bg-red-950/40 hover:bg-red-200 dark:hover:bg-red-900/40" : "bg-emerald-50 dark:bg-emerald-950/30 hover:bg-emerald-100 dark:hover:bg-emerald-900/40"}`}
                                title={isStop ? "Satış kapalı - tıklayarak açın · Sürükle: aralık seç" : "Satışa açık - tıklayarak kapatın · Sürükle: aralık seç"}
                            >
                                {isStop ? <X className="h-3.5 w-3.5 text-red-600 dark:text-red-400" /> : <Check className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />}
                                {entry?.closed_to_arrival && <span data-testid={`cta-badge-${plan.id}-${ds}`} title="Girişe kapalı" className="ml-0.5 px-1 rounded bg-orange-500 text-white text-[9px] font-bold leading-4">G</span>}
                                {entry?.closed_to_departure && <span data-testid={`ctd-badge-${plan.id}-${ds}`} title="Çıkışa kapalı" className="ml-0.5 px-1 rounded bg-violet-500 text-white text-[9px] font-bold leading-4">Ç</span>}
                            </button>
                        </td>
                    );
                }
                if (fieldType === "discount") {
                    return (
                        <td key={ds} className={`${cellBase} p-0`}>
                            <DiscountCell
                                entry={entry}
                                onSave={(payload) => patchCell(ds, roomTypeId, plan.id, payload)}
                                onMouseDown={() => startDragSelect(plan.id, roomTypeId, fieldType, ds)}
                                onMouseEnter={() => dragExtend(plan.id, roomTypeId, fieldType, ds)}
                                wasMultiDrag={wasMultiDrag}
                                testId={`discount-${plan.id}-${ds}`}
                            />
                        </td>
                    );
                }
                if (fieldType === "release") {
                    const rd = Number(entry?.release_days || 0);
                    const handleSave = (v) => {
                        const n = v === "" || v == null ? 0 : Math.max(0, Number(v) || 0);
                        patchCell(ds, roomTypeId, plan.id, { release_days: n });
                    };
                    return (
                        <td key={ds} className={`${cellBase} p-0`}>
                            <NumericCell
                                value={rd > 0 ? rd : null}
                                placeholder="—"
                                fieldType="release_days"
                                onCommit={handleSave}
                                onShiftClick={() => extendSelection(plan.id, roomTypeId, fieldType, ds)}
                                onClickAnchor={() => setAnchorOnly(plan.id, roomTypeId, fieldType, ds)}
                                onMouseDown={() => startDragSelect(plan.id, roomTypeId, fieldType, ds)}
                                onMouseEnter={() => dragExtend(plan.id, roomTypeId, fieldType, ds)}
                                wasMultiDrag={wasMultiDrag}
                                testId={`release-${plan.id}-${ds}`}
                                badgeClass={rd > 0 ? "bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-bold" : ""}
                                suffix={rd > 0 ? "g" : ""}
                            />
                        </td>
                    );
                }
                // numeric cell — value may be in new `price` field or legacy fallback
                let value = entry?.[fieldType];
                if (fieldType === "price" && (value == null || value === 0)) {
                    value = entry?.price_2 ?? entry?.price_1 ?? null;
                }
                // Fallback: when a per-capacity field is empty, derive from legacy `price`/`cost_price`
                if (fieldType === "price_2" && (value == null || value === 0)) {
                    value = entry?.price ?? null;
                }
                if (fieldType === "cost_price_2" && (value == null || value === 0)) {
                    value = entry?.cost_price ?? null;
                }
                const placeholder = fieldType === "quota" ? "—"
                    : fieldType === "price" ? (plan.price_2_person || plan.price_1_person || "")
                    : fieldType === "price_1" ? (plan.price_1_person || "")
                    : fieldType === "price_2" ? (plan.price_2_person || "")
                    : fieldType === "price_3" ? (plan.price_3_person || "")
                    : fieldType === "cost_price" ? ""
                    : "";
                return (
                    <td key={ds} className={`${cellBase} p-0`}>
                        <NumericCell
                            value={value}
                            placeholder={placeholder}
                            fieldType={fieldType}
                            onCommit={(v) => patchCell(ds, roomTypeId, plan.id, { [fieldType]: v })}
                            onShiftClick={() => extendSelection(plan.id, roomTypeId, fieldType, ds)}
                            onClickAnchor={() => setAnchorOnly(plan.id, roomTypeId, fieldType, ds)}
                            onMouseDown={() => startDragSelect(plan.id, roomTypeId, fieldType, ds)}
                            onMouseEnter={() => dragExtend(plan.id, roomTypeId, fieldType, ds)}
                            wasMultiDrag={wasMultiDrag}
                            testId={`${fieldType}-${plan.id}-${ds}`}
                        />
                    </td>
                );
            })}
        </tr>
    );
}

/* ====================== DISCOUNT CELL ====================== */
function DiscountCell({ entry, onSave, onMouseDown, onMouseEnter, wasMultiDrag, testId }) {
    const [open, setOpen] = useState(false);

    // Tier list (new) with legacy single-discount fallback
    const tiers = (Array.isArray(entry?.discount_tiers) && entry.discount_tiers.length > 0)
        ? entry.discount_tiers
        : (entry?.discount_type && entry?.discount_value != null
            ? [{ type: entry.discount_type, value: entry.discount_value, label: entry.discount_label || "", min_nights: Number(entry.discount_min_nights || 0) }]
            : []);

    const formatTier = (t) => t.type === "percent" ? `%${t.value}` : t.type === "fixed" ? `-${Number(t.value).toLocaleString("tr-TR")}₺` : `→${Number(t.value).toLocaleString("tr-TR")}₺`;
    const sortedTiers = [...tiers].sort((a, b) => (Number(a.min_nights || 0) - Number(b.min_nights || 0)));
    const display = sortedTiers.length === 0
        ? ""
        : sortedTiers.length === 1
            ? formatTier(sortedTiers[0])
            : sortedTiers.map(formatTier).join("/");
    const primaryType = sortedTiers[0]?.type;

    const colorCls = primaryType === "percent" ? "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/30"
        : primaryType === "fixed" ? "text-fuchsia-700 dark:text-fuchsia-300 bg-fuchsia-50 dark:bg-fuchsia-950/30"
        : primaryType === "override" ? "text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-950/30"
        : "";

    const tooltip = sortedTiers.length > 0
        ? sortedTiers.map((t) => `${Number(t.min_nights) > 0 ? `Min ${t.min_nights} gece → ` : "Tüm rezervasyonlar → "}${formatTier(t)}${t.label ? ` (${t.label})` : ""}`).join("\n") + "\n· Tıkla düzenle"
        : "Tıkla: indirim ekle";

    return (
        <>
            <button
                onMouseDown={(e) => { if (e.button !== 0 || e.shiftKey) return; onMouseDown && onMouseDown(); }}
                onMouseEnter={() => onMouseEnter && onMouseEnter()}
                onClick={(e) => {
                    if (e.shiftKey) return;
                    if (wasMultiDrag && wasMultiDrag()) return;
                    setOpen(true);
                }}
                data-testid={testId}
                className={`w-full h-7 px-1 text-[11px] font-bold hover:bg-amber-50 dark:hover:bg-amber-950/30 transition-colors ${colorCls}`}
                title={tooltip}
            >
                {display || <span className="text-[10px] text-slate-300 dark:text-slate-700">+</span>}
            </button>
            {open && <DiscountPopover entry={entry} onClose={() => setOpen(false)} onSave={onSave} />}
        </>
    );
}

function DiscountPopover({ entry, onClose, onSave }) {
    const existingTiers = (Array.isArray(entry?.discount_tiers) && entry.discount_tiers.length > 0)
        ? entry.discount_tiers
        : (entry?.discount_type && entry?.discount_value != null
            ? [{ type: entry.discount_type, value: entry.discount_value, label: entry.discount_label || "", min_nights: Number(entry.discount_min_nights || 0) }]
            : []);
    const sortedExisting = [...existingTiers].sort((a, b) => (Number(a.min_nights || 0) - Number(b.min_nights || 0)));

    const [type, setType] = useState("percent");
    const [value, setValue] = useState("");
    const [label, setLabel] = useState("");
    const [minNights, setMinNights] = useState("");
    const [saving, setSaving] = useState(false);

    const formatTier = (t) => t.type === "percent" ? `%${t.value}` : t.type === "fixed" ? `-${Number(t.value).toLocaleString("tr-TR")}₺` : `→${Number(t.value).toLocaleString("tr-TR")}₺`;

    const addTier = async () => {
        setSaving(true);
        try {
            await onSave({
                discount_type: type,
                discount_value: Number(value),
                discount_label: label || "",
                discount_min_nights: minNights ? Number(minNights) : null,
            });
            setValue(""); setLabel(""); setMinNights("");
            // keep popover open so the user sees the new tier in the list and can add more
        } finally {
            setSaving(false);
        }
    };

    const removeTier = async (tier) => {
        setSaving(true);
        try {
            await onSave({
                discount_type: "clear",
                discount_value: null,
                discount_label: null,
                discount_min_nights: Number(tier.min_nights || 0) || null,
            });
        } finally {
            setSaving(false);
        }
    };

    const clearAll = async () => {
        setSaving(true);
        try {
            await onSave({
                discount_type: "clear",
                discount_value: null,
                discount_label: null,
                discount_min_nights: null,
            });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-5 w-[min(460px,92vw)] max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Percent className="h-4 w-4 text-amber-500" />
                        <h3 className="font-display text-sm font-bold">İndirim Kademeleri</h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                </div>

                {/* Existing tiers list */}
                {sortedExisting.length > 0 && (
                    <div className="mb-4">
                        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Mevcut Kademeler</div>
                        <div className="space-y-1.5" data-testid="existing-tiers">
                            {sortedExisting.map((t, i) => (
                                <div key={`${t.type}-${t.min_nights}-${i}`} data-testid={`tier-row-${t.min_nights || 0}`} className="flex items-center gap-2 p-2 rounded-lg bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
                                    <div className="flex-1 text-xs">
                                        <span className="font-bold text-amber-700 dark:text-amber-300">{formatTier(t)}</span>
                                        <span className="text-slate-600 dark:text-slate-400 ml-2">
                                            {Number(t.min_nights) > 0 ? `· Min ${t.min_nights} gece` : "· Tüm rezervasyonlar"}
                                        </span>
                                        {t.label && <span className="text-slate-500 ml-2">· {t.label}</span>}
                                    </div>
                                    <button
                                        onClick={() => removeTier(t)}
                                        disabled={saving}
                                        data-testid={`remove-tier-${t.min_nights || 0}`}
                                        className="p-1.5 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 hover:bg-red-100 disabled:opacity-50"
                                        title="Bu kademeyi kaldır"
                                    >
                                        <X className="h-3.5 w-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <p className="text-[11px] text-slate-500 mt-2">
                            Sistem rezervasyondaki gece sayısına göre <b>en yüksek tutarda kazandıran kademeyi</b> otomatik uygular.
                        </p>
                    </div>
                )}

                {/* Add new tier */}
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-2">Yeni Kademe Ekle</div>
                    <div className="space-y-3">
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Tip</label>
                            <div className="grid grid-cols-3 gap-1.5">
                                {[
                                    { v: "percent", l: "Yüzde %" },
                                    { v: "fixed", l: "Sabit ₺" },
                                    { v: "override", l: "Yeni Fiyat" },
                                ].map((o) => (
                                    <button key={o.v} onClick={() => setType(o.v)} data-testid={`cell-disc-type-${o.v}`}
                                        className={`px-2 py-1.5 rounded-lg text-[11px] font-bold ${type === o.v ? "bg-amber-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                        {o.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{type === "percent" ? "Yüzde (%)" : type === "fixed" ? "Tutar (₺)" : "Yeni Fiyat (₺)"}</label>
                                <input data-testid="cell-disc-value" type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)} className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                            </div>
                            <div>
                                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Min. Kalış (gece)</label>
                                <input data-testid="cell-disc-min-nights" type="number" min="0" value={minNights} onChange={(e) => setMinNights(e.target.value)} placeholder="0" className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                            </div>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Açıklama (opsiyonel)</label>
                            <input data-testid="cell-disc-label" value={label} onChange={(e) => setLabel(e.target.value)} placeholder='Örn: "Erken Rezervasyon"' className="w-full px-2 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm" />
                        </div>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-2">
                        Aynı <b>Min. Kalış</b> değerine sahip bir kademe varsa o kademe güncellenir. Farklıysa yeni kademe eklenir.
                    </p>
                </div>

                <div className="flex gap-2 mt-5">
                    {sortedExisting.length > 0 && (
                        <button onClick={clearAll} disabled={saving} data-testid="cell-disc-clear-all" className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/40 text-red-600 text-xs font-bold hover:bg-red-100 disabled:opacity-50">Hepsini Sil</button>
                    )}
                    <button onClick={addTier} disabled={saving || !value} data-testid="cell-disc-save" className="flex-1 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 disabled:bg-slate-300 text-white text-xs font-bold">{saving ? "Kaydediliyor..." : "Kademe Ekle"}</button>
                </div>
            </div>
        </div>
    );
}

function NumericCell({ value, placeholder, fieldType, onCommit, onShiftClick, onClickAnchor, onMouseDown, onMouseEnter, wasMultiDrag, testId, badgeClass = "", suffix = "" }) {
    const [editing, setEditing] = useState(false);
    const [val, setVal] = useState("");

    const commit = () => {
        const num = val === "" ? null : Number(val);
        if (num != null && !isNaN(num) && num !== value) onCommit(num);
        setEditing(false);
    };

    if (editing) {
        return (
            <input
                type="number"
                autoFocus
                value={val}
                onChange={(e) => setVal(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") setEditing(false); }}
                className="w-full h-7 px-1 text-[11px] text-center border-2 border-sky-400 outline-none bg-white dark:bg-slate-900"
                data-testid={`${testId}-input`}
            />
        );
    }
    const display = value != null ? value : "";
    const isPrice = fieldType === "price" || fieldType === "cost_price" || fieldType.startsWith("price_");
    const isCost = fieldType === "cost_price";
    return (
        <button
            onMouseDown={(e) => {
                if (e.button !== 0 || e.shiftKey) return;
                onMouseDown && onMouseDown();
            }}
            onMouseEnter={() => onMouseEnter && onMouseEnter()}
            onClick={(e) => {
                if (e.shiftKey) { onShiftClick && onShiftClick(); return; }
                if (wasMultiDrag && wasMultiDrag()) return; // drag-select, no editing
                onClickAnchor && onClickAnchor();
                setVal(value != null ? String(value) : "");
                setEditing(true);
            }}
            data-testid={testId}
            className={`w-full h-7 px-1 text-[11px] hover:bg-sky-50 dark:hover:bg-sky-950/30 transition-colors ${badgeClass}`}
            title="Tıkla: tek hücre düzenle · Sürükle: aralık seç"
        >
            {display !== "" ? (
                <span className={`font-semibold ${isCost ? "text-amber-700 dark:text-amber-300" : isPrice ? "text-sky-700 dark:text-sky-300" : "text-slate-800 dark:text-slate-200"}`}>
                    {isPrice ? `${Number(display).toLocaleString("tr-TR")}` : `${display}${suffix}`}
                </span>
            ) : (
                <span className="text-[10px] text-slate-400 italic">{placeholder ? `(${typeof placeholder === "number" ? placeholder.toLocaleString("tr-TR") : placeholder})` : "—"}</span>
            )}
        </button>
    );
}

function BulkUpdateDrawer({ hotel, plans, hotelId, onClose, onSaved }) {
    const today = ymd(new Date());
    const monthAhead = ymd(addDays(new Date(), 30));
    const isNet = (hotel.pricing_type || "commission") === "net";
    const commissionPct = Number(hotel.commission_percent || 0);
    const roomTypes = hotel.room_types || [];

    const [planId, setPlanId] = useState(plans[0]?.id || "");
    const [operation, setOperation] = useState("price"); // price | quota | stop_sale | min_max | profit_margin
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(monthAhead);
    const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5, 6]);
    const [markupOn, setMarkupOn] = useState(true);
    const [markupPct, setMarkupPct] = useState(commissionPct > 0 ? commissionPct : 15);
    // Bulk-profit-margin %: takes existing cost prices on the calendar and rewrites
    // sale prices as cost × (1 + marginPct/100).
    const [bulkMarginPct, setBulkMarginPct] = useState(commissionPct > 0 ? commissionPct : 20);
    // Release period (days). 0 = no release (sellable up to check-in day).
    const [releaseDays, setReleaseDays] = useState(2);

    // Margin-based sale price: sale = cost / (1 - margin%/100)
    // Example: cost 1000, margin 25 → 1000 / 0.75 = 1333.33
    const applyMargin = (cost) => {
        const c = Number(cost);
        const pct = Number(markupPct) || 0;
        if (!c || isNaN(c)) return 0;
        const denom = 1 - pct / 100;
        if (denom <= 0) return c; // safeguard for invalid margins ≥ 100%
        return Math.round(c / denom);
    };

    // selected room ids
    const [selectedRoomIds, setSelectedRoomIds] = useState([]);
    // per-room data: rid -> { prices: {1: '', 2: ''}, cost_prices: {1: '', 2: ''}, quota, stop_sale, min, max }
    const [rows, setRows] = useState({});
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState("");

    const selectedPlan = plans.find((p) => p.id === planId);
    const planRoomIds = selectedPlan
        ? (selectedPlan.room_type_ids && selectedPlan.room_type_ids.length
            ? selectedPlan.room_type_ids
            : (selectedPlan.room_type_id ? [selectedPlan.room_type_id] : []))
        : [];
    const planRooms = roomTypes.filter((r) => planRoomIds.includes(r.id));

    const getRoomMode = (rid) => {
        const m = (selectedPlan?.room_pricing_modes || []).find((x) => x.room_type_id === rid);
        return m?.mode || "manual";
    };
    const getRoomMultipliers = (rid) => {
        const m = (selectedPlan?.room_pricing_modes || []).find((x) => x.room_type_id === rid);
        return m?.multipliers || {};
    };

    useEffect(() => {
        // when plan changes: reset selection to all rooms of plan
        setSelectedRoomIds(planRoomIds);
        // eslint-disable-next-line
    }, [planId]);

    const toggleRoom = (rid) => {
        setSelectedRoomIds((s) => s.includes(rid) ? s.filter((x) => x !== rid) : [...s, rid]);
    };
    const toggleAll = () => {
        if (selectedRoomIds.length === planRoomIds.length) setSelectedRoomIds([]);
        else setSelectedRoomIds(planRoomIds);
    };

    const updateRowPrice = (rid, k, v) => {
        setRows((r) => {
            const cur = { ...(r[rid] || {}) };
            cur.prices = { ...(cur.prices || {}), [k]: v };
            // auto-calc sale from cost if net & markupOn
            if (isNet && markupOn) {
                // skipped here — handled in cost handler
            }
            return { ...r, [rid]: cur };
        });
    };
    const updateRowCost = (rid, k, v) => {
        setRows((r) => {
            const cur = { ...(r[rid] || {}) };
            cur.cost_prices = { ...(cur.cost_prices || {}), [k]: v };
            // auto-calc sale price for the same k when markupOn
            if (isNet && markupOn) {
                const cost = Number(v);
                if (!isNaN(cost) && cost > 0) {
                    cur.prices = { ...(cur.prices || {}), [k]: String(applyMargin(cost)) };
                }
            }
            return { ...r, [rid]: cur };
        });
    };
    const updateRowField = (rid, field, v) => {
        setRows((r) => ({ ...r, [rid]: { ...(r[rid] || {}), [field]: v } }));
    };
    const updateBase = (rid, kind, v) => {
        // kind: "base_price" | "base_cost_price" | "flat_price" | "flat_cost_price"
        setRows((r) => {
            const cur = { ...(r[rid] || {}), [kind]: v };
            // If net + markup on + entering cost → auto-fill price
            if (isNet && markupOn) {
                if (kind === "base_cost_price" && v !== "" && !isNaN(Number(v))) {
                    cur.base_price = String(applyMargin(v));
                }
                if (kind === "flat_cost_price" && v !== "" && !isNaN(Number(v))) {
                    cur.flat_price = String(applyMargin(v));
                }
            }
            return { ...r, [rid]: cur };
        });
    };

    const toggleWd = (w) => setWeekdays((wd) => wd.includes(w) ? wd.filter((x) => x !== w) : [...wd, w]);

    const submit = async () => {
        if (!planId) { setMsg("Önce fiyat planı seçin"); return; }
        if (selectedRoomIds.length === 0) { setMsg("En az bir oda seçin"); return; }

        // Bulk profit-margin: doesn't need any per-room input — backend reads existing costs
        if (operation === "profit_margin") {
            const pct = Number(bulkMarginPct);
            if (isNaN(pct) || pct < 0) { setMsg("Geçerli bir kar marjı (%) girin"); return; }
            setSaving(true); setMsg("");
            try {
                const r = await api.post(`/admin/hotels/${hotelId}/calendar/bulk`, {
                    hotel_id: hotelId,
                    start_date: startDate,
                    end_date: endDate,
                    weekdays,
                    room_type_ids: selectedRoomIds,
                    rate_plan_ids: [planId],
                    operation: "profit_margin",
                    profit_margin_percent: pct,
                });
                setMsg(`✓ ${r.data.upserts} hücre güncellendi. Mevcut maliyetler üzerinden satış fiyatları yeniden hesaplandı.`);
                setTimeout(() => onSaved(), 1100);
            } catch (e) {
                setMsg(e.response?.data?.detail || "Hata");
            } finally {
                setSaving(false);
            }
            return;
        }

        // Bulk release: just hits the dedicated /bulk endpoint with operation=release
        if (operation === "release") {
            const rd = Number(releaseDays);
            if (isNaN(rd) || rd < 0) { setMsg("Release süresi 0 veya pozitif olmalı"); return; }
            setSaving(true); setMsg("");
            try {
                const r = await api.post(`/admin/hotels/${hotelId}/calendar/bulk`, {
                    hotel_id: hotelId,
                    start_date: startDate,
                    end_date: endDate,
                    weekdays,
                    room_type_ids: selectedRoomIds,
                    rate_plan_ids: [planId],
                    operation: "release",
                    release_days: rd,
                });
                setMsg(rd === 0
                    ? `✓ ${r.data.upserts} hücreden release kaldırıldı.`
                    : `✓ ${r.data.upserts} hücreye ${rd} gün release uygulandı.`);
                setTimeout(() => onSaved(), 1100);
            } catch (e) {
                setMsg(e.response?.data?.detail || "Hata");
            } finally {
                setSaving(false);
            }
            return;
        }

        const roomsPayload = [];
        for (const rid of selectedRoomIds) {
            const rt = planRooms.find((x) => x.id === rid);
            if (!rt) continue;
            const row = rows[rid] || {};
            const entry = { room_type_id: rid };
            let has = false;
            if (operation === "price") {
                const mode = getRoomMode(rid);
                const cap = rt.capacity || 2;
                const prices = {};
                const cost_prices = {};
                if (mode === "multiplier") {
                    // Single base "kişi başı"
                    const mults = getRoomMultipliers(rid);
                    const base = row.base_price;
                    const baseCost = row.base_cost_price;
                    if (base !== "" && base != null && !isNaN(Number(base))) {
                        for (let k = 1; k <= cap; k++) {
                            const mk = Number(mults[k] ?? mults[String(k)] ?? 0);
                            if (mk > 0) prices[String(k)] = Math.round(Number(base) * mk);
                        }
                    }
                    if (isNet && baseCost !== "" && baseCost != null && !isNaN(Number(baseCost))) {
                        for (let k = 1; k <= cap; k++) {
                            const mk = Number(mults[k] ?? mults[String(k)] ?? 0);
                            if (mk > 0) cost_prices[String(k)] = Math.round(Number(baseCost) * mk);
                        }
                    }
                } else if (mode === "flat") {
                    const v = row.flat_price;
                    const cv = row.flat_cost_price;
                    if (v !== "" && v != null && !isNaN(Number(v))) {
                        for (let k = 1; k <= cap; k++) prices[String(k)] = Number(v);
                    }
                    if (isNet && cv !== "" && cv != null && !isNaN(Number(cv))) {
                        for (let k = 1; k <= cap; k++) cost_prices[String(k)] = Number(cv);
                    }
                } else {
                    // manual
                    for (let k = 1; k <= cap; k++) {
                        const pv = row.prices?.[k];
                        if (pv !== "" && pv != null && !isNaN(Number(pv))) {
                            prices[String(k)] = Number(pv);
                        }
                        if (isNet) {
                            const cv = row.cost_prices?.[k];
                            if (cv !== "" && cv != null && !isNaN(Number(cv))) {
                                cost_prices[String(k)] = Number(cv);
                            }
                        }
                    }
                }
                if (Object.keys(prices).length > 0) { entry.prices = prices; has = true; }
                if (Object.keys(cost_prices).length > 0) { entry.cost_prices = cost_prices; has = true; }
            } else if (operation === "quota") {
                if (row.quota !== "" && row.quota != null && !isNaN(Number(row.quota))) {
                    entry.quota = Number(row.quota); has = true;
                }
            } else if (operation === "stop_sale") {
                for (const f of ["stop_sale", "closed_to_arrival", "closed_to_departure"]) {
                    if (row[f] != null) { entry[f] = !!row[f]; has = true; }
                }
            } else if (operation === "min_max") {
                if (row.min !== "" && row.min != null && !isNaN(Number(row.min))) {
                    entry.min_nights = Number(row.min); has = true;
                }
                if (row.max !== "" && row.max != null && !isNaN(Number(row.max))) {
                    entry.max_nights = Number(row.max); has = true;
                }
            }
            if (has) roomsPayload.push(entry);
        }

        if (roomsPayload.length === 0) {
            setMsg("Seçili odalara değer girin");
            return;
        }

        setSaving(true);
        setMsg("");
        try {
            const r = await api.post(`/admin/hotels/${hotelId}/calendar/bulk-rooms`, {
                start_date: startDate,
                end_date: endDate,
                weekdays,
                rate_plan_id: planId,
                rooms: roomsPayload,
            });
            setMsg(`✓ ${r.data.upserts} kayıt güncellendi.`);
            setTimeout(() => onSaved(), 800);
        } catch (e) {
            setMsg(e.response?.data?.detail || "Hata");
        } finally {
            setSaving(false);
        }
    };

    const OP_CHIPS = [
        { v: "price", l: "Fiyat" },
        { v: "quota", l: "Kontenjan" },
        { v: "stop_sale", l: "Stop / Giriş / Çıkış" },
        { v: "release", l: "Release Süresi" },
        { v: "min_max", l: "Min/Max Gece" },
        { v: "profit_margin", l: "Kar Marjı (Otomatik)" },
    ];

    return (
        <div className="fixed inset-0 z-50 flex" onClick={onClose}>
            <div className="flex-1 bg-black/40" />
            <div className="w-full max-w-3xl bg-white dark:bg-slate-900 h-full overflow-y-auto flex flex-col" onClick={(e) => e.stopPropagation()}>
                <div className="px-5 pt-5 pb-3 border-b border-slate-200 dark:border-slate-800 flex items-start justify-between">
                    <div>
                        <h2 className="font-display text-lg font-bold text-slate-900 dark:text-white">Toplu Güncelleme</h2>
                        <p className="text-[11px] text-slate-500 mt-0.5">Plan → ne güncelleyeceğini seç → odaları işaretle → değerleri gir → tarih aralığı.</p>
                    </div>
                    <button onClick={onClose} data-testid="bulk-close-btn" className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-4 w-4" /></button>
                </div>

                <div className="flex-1 px-5 py-4 space-y-4">
                    {/* Step 1: Plan select */}
                    <Section step="1" title="Fiyat Planı">
                        <select
                            data-testid="bulk-plan-select"
                            value={planId}
                            onChange={(e) => setPlanId(e.target.value)}
                            className={INPUT_SM}
                        >
                            <option value="">— Plan seçin —</option>
                            {plans.map((p) => (
                                <option key={p.id} value={p.id}>{p.code} · {p.name} ({p.board})</option>
                            ))}
                        </select>
                    </Section>

                    {/* Step 2: Operation chips */}
                    {planId && (
                        <Section step="2" title="Ne güncellemek istiyorsun?">
                            <div className="flex flex-wrap gap-1.5">
                                {OP_CHIPS.map((op) => (
                                    <button
                                        key={op.v}
                                        type="button"
                                        onClick={() => setOperation(op.v)}
                                        data-testid={`op-${op.v}`}
                                        className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${operation === op.v ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                                    >{op.l}</button>
                                ))}
                            </div>
                        </Section>
                    )}

                    {/* Step 2b: Net markup */}
                    {planId && operation === "price" && isNet && (
                        <div className="rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-900/40 p-3">
                            <div className="flex items-center gap-3 flex-wrap">
                                <label className="inline-flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={markupOn}
                                        onChange={(e) => setMarkupOn(e.target.checked)}
                                        className="h-4 w-4 accent-amber-500"
                                        data-testid="bulk-markup-toggle"
                                    />
                                    <span className="text-xs font-bold text-amber-900 dark:text-amber-200">Kar Marjı (%) ile satış otomatik</span>
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    max="99"
                                    step="0.1"
                                    value={markupPct}
                                    onChange={(e) => setMarkupPct(e.target.value)}
                                    disabled={!markupOn}
                                    className="w-20 px-2 py-1 rounded-lg bg-white dark:bg-slate-800 border border-amber-300 dark:border-amber-700 text-xs font-semibold text-slate-900 dark:text-white disabled:opacity-50"
                                    data-testid="bulk-markup-pct"
                                />
                                <span className="text-[11px] text-amber-700 dark:text-amber-300">% (otelin komisyon: {commissionPct}%)</span>
                            </div>
                            <p className="text-[10px] text-amber-700 dark:text-amber-300 mt-1">Satış = Maliyet ÷ (1 − Marj%). Örn: maliyet 1000 + marj 25% → satış 1.333 TL.</p>
                        </div>
                    )}

                    {/* Step 2c: Bulk profit margin — uses existing cost prices already in the calendar */}
                    {planId && operation === "profit_margin" && (
                        <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-900/40 p-4 space-y-2.5" data-testid="bulk-profit-margin-block">
                            <div className="flex items-start gap-2">
                                <div className="h-6 w-6 rounded-lg bg-emerald-500 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">%</div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-bold text-emerald-900 dark:text-emerald-200">Toplu Kar Marjı (Otomatik)</h3>
                                    <p className="text-[11px] text-emerald-700 dark:text-emerald-300 mt-0.5">
                                        Takvimde önceden girilmiş <b>maliyet (net)</b> fiyatları okunur, seçtiğiniz <b>marj %</b> kadar üstüne eklenip <b>satış fiyatları</b> olarak otomatik yazılır. Tek tek fiyat girmenize gerek yok.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap pl-8">
                                <label className="text-xs font-bold uppercase tracking-widest text-emerald-800 dark:text-emerald-300">Kar Marjı %</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="500"
                                    step="0.1"
                                    value={bulkMarginPct}
                                    onChange={(e) => setBulkMarginPct(e.target.value)}
                                    className="w-24 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border-2 border-emerald-300 dark:border-emerald-800 text-sm font-bold text-slate-900 dark:text-white"
                                    data-testid="bulk-profit-margin-pct"
                                />
                                <span className="text-[11px] text-emerald-700 dark:text-emerald-300">
                                    Formül: <b>Satış = Maliyet × (1 + Marj/100)</b>
                                    {bulkMarginPct && !isNaN(Number(bulkMarginPct)) && (
                                        <> &nbsp;·&nbsp; Örnek: 1000 ₺ × {(1 + Number(bulkMarginPct) / 100).toFixed(2)} = <b>{Math.round(1000 * (1 + Number(bulkMarginPct) / 100)).toLocaleString("tr-TR")} ₺</b></>
                                    )}
                                </span>
                            </div>
                            <p className="text-[10px] text-emerald-700/80 dark:text-emerald-400/70 pl-8">
                                ⚠️ Maliyet (cost_price) girilmemiş hücreler atlanır — sıfırdan fiyat üretilmez.
                            </p>
                        </div>
                    )}

                    {/* Step 2d: Bulk release period */}
                    {planId && operation === "release" && (
                        <div className="rounded-xl bg-purple-50 dark:bg-purple-950/20 border border-purple-200 dark:border-purple-900/40 p-4 space-y-2.5" data-testid="bulk-release-block">
                            <div className="flex items-start gap-2">
                                <div className="h-6 w-6 rounded-lg bg-purple-500 text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0">R</div>
                                <div className="flex-1 min-w-0">
                                    <h3 className="text-sm font-bold text-purple-900 dark:text-purple-200">Release Süresi (Gün)</h3>
                                    <p className="text-[11px] text-purple-700 dark:text-purple-300 mt-0.5">
                                        Bir geceyi giriş tarihinden <b>kaç gün önce</b> satışa kapatmak istediğinizi belirler. Örn: <b>3 gün</b> → 12 Haziran için yapılan rezervasyonlar 9 Haziran 23.59&apos;dan sonra alınmaz. <b>0</b> girerseniz release tamamen kaldırılır.
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap pl-8">
                                <label className="text-xs font-bold uppercase tracking-widest text-purple-800 dark:text-purple-300">Release (gün)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="365"
                                    step="1"
                                    value={releaseDays}
                                    onChange={(e) => setReleaseDays(e.target.value)}
                                    className="w-24 px-2.5 py-1.5 rounded-lg bg-white dark:bg-slate-800 border-2 border-purple-300 dark:border-purple-800 text-sm font-bold text-slate-900 dark:text-white"
                                    data-testid="bulk-release-days"
                                />
                                <span className="text-[11px] text-purple-700 dark:text-purple-300">
                                    {Number(releaseDays) > 0
                                        ? <>Bugünden itibaren ilk <b>{releaseDays} gün</b> içindeki tüm geceler kapanır.</>
                                        : <b>Release kaldırılacak (sınırsız satış)</b>}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Rooms table */}
                    {planId && (
                        <Section step="3" title="Odalar">
                            {planRooms.length === 0 ? (
                                <p className="text-xs text-slate-500 italic">Bu plana atanmış oda yok.</p>
                            ) : (
                                <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto">
                                    <table className="w-full text-[11px]">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-800 text-[10px] uppercase tracking-wider text-slate-500">
                                                <th className="text-center px-2 py-1.5 font-bold w-8">
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedRoomIds.length === planRoomIds.length && planRoomIds.length > 0}
                                                        onChange={toggleAll}
                                                        className="h-3.5 w-3.5 accent-sky-500"
                                                        data-testid="bulk-select-all"
                                                    />
                                                </th>
                                                <th className="text-left px-2 py-1.5 font-bold">Oda</th>
                                                <th className="text-left px-2 py-1.5 font-bold">
                                                    {operation === "price" ? "Fiyatlar"
                                                        : operation === "quota" ? "Kontenjan"
                                                        : operation === "stop_sale" ? "Stop · Girişe Kapat · Çıkışa Kapat"
                                                        : operation === "profit_margin" ? "Etkilenen"
                                                        : operation === "release" ? "Etkilenen"
                                                        : "Min / Max Gece"}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {planRooms.map((rt) => {
                                                const selected = selectedRoomIds.includes(rt.id);
                                                const cap = rt.capacity || 2;
                                                const row = rows[rt.id] || {};
                                                return (
                                                    <tr key={rt.id} data-testid={`bulk-row-${rt.id}`} className={`border-t border-slate-100 dark:border-slate-800 ${selected ? "" : "opacity-50"}`}>
                                                        <td className="px-2 py-1.5 text-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selected}
                                                                onChange={() => toggleRoom(rt.id)}
                                                                className="h-3.5 w-3.5 accent-sky-500"
                                                                data-testid={`bulk-room-cb-${rt.id}`}
                                                            />
                                                        </td>
                                                        <td className="px-2 py-1.5">
                                                            <div className="font-semibold text-slate-900 dark:text-white">{rt.name}</div>
                                                            <div className="text-[10px] text-slate-500 flex items-center gap-1.5">
                                                                <span>{cap} kişilik</span>
                                                                <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${getRoomMode(rt.id) === "multiplier" ? "bg-purple-100 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300" : getRoomMode(rt.id) === "flat" ? "bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" : "bg-slate-200 dark:bg-slate-700 text-slate-600 dark:text-slate-300"}`}>
                                                                    {getRoomMode(rt.id) === "multiplier" ? "Çarpanlı" : getRoomMode(rt.id) === "flat" ? "Ünite" : "Manuel"}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-2 py-1.5">
                                                            {!selected ? (
                                                                <span className="text-[10px] italic text-slate-400">— seçili değil —</span>
                                                            ) : operation === "price" ? (
                                                                (() => {
                                                                    const mode = getRoomMode(rt.id);
                                                                    if (mode === "multiplier") {
                                                                        const mults = getRoomMultipliers(rt.id);
                                                                        const base = row.base_price;
                                                                        const baseCost = row.base_cost_price;
                                                                        return (
                                                                            <div className="flex items-end gap-3 flex-wrap">
                                                                                <div className="flex flex-col items-center gap-0.5">
                                                                                    <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Kişi Başı</span>
                                                                                    {isNet && (
                                                                                        <input
                                                                                            type="number"
                                                                                            min="0"
                                                                                            value={baseCost ?? ""}
                                                                                            onChange={(e) => updateBase(rt.id, "base_cost_price", e.target.value)}
                                                                                            placeholder="Mlyt"
                                                                                            className="w-20 px-1 py-1 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] font-semibold text-center text-amber-800 dark:text-amber-200"
                                                                                            data-testid={`bulk-base-cost-${rt.id}`}
                                                                                        />
                                                                                    )}
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        value={base ?? ""}
                                                                                        onChange={(e) => updateBase(rt.id, "base_price", e.target.value)}
                                                                                        placeholder={isNet ? "Satış" : "Fiyat"}
                                                                                        className="w-20 px-1 py-1 rounded bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-[11px] font-semibold text-center text-sky-800 dark:text-sky-200"
                                                                                        data-testid={`bulk-base-${rt.id}`}
                                                                                    />
                                                                                </div>
                                                                                <div className="flex flex-col gap-0.5 text-[10px] text-slate-500">
                                                                                    <span className="font-bold uppercase tracking-wider text-[9px]">Hesaplanan Fiyatlar</span>
                                                                                    <div className="flex flex-wrap gap-1.5">
                                                                                        {Array.from({ length: cap }, (_, i) => i + 1).map((k) => {
                                                                                            const mk = Number(mults[k] ?? mults[String(k)] ?? 0);
                                                                                            const calcSale = (base !== "" && base != null && !isNaN(Number(base)) && mk > 0) ? Math.round(Number(base) * mk) : null;
                                                                                            const calcCost = (isNet && baseCost !== "" && baseCost != null && !isNaN(Number(baseCost)) && mk > 0) ? Math.round(Number(baseCost) * mk) : null;
                                                                                            const active = calcSale != null || calcCost != null;
                                                                                            return (
                                                                                                <div
                                                                                                    key={k}
                                                                                                    data-testid={`bulk-calc-${rt.id}-${k}`}
                                                                                                    className={`px-2 py-1 rounded-md border ${active ? "bg-sky-50 dark:bg-sky-950/40 border-sky-200 dark:border-sky-900" : "bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700"}`}
                                                                                                >
                                                                                                    <div className={`text-[9px] font-bold uppercase tracking-wider ${active ? "text-sky-700 dark:text-sky-300" : "text-slate-400"}`}>
                                                                                                        {k} Kişi Fiyatı
                                                                                                    </div>
                                                                                                    <div className={`text-[11px] font-bold ${active ? "text-sky-800 dark:text-sky-200" : "text-slate-400"}`}>
                                                                                                        {calcSale != null ? `${calcSale.toLocaleString("tr-TR")} ₺` : "—"}
                                                                                                    </div>
                                                                                                    {isNet && (
                                                                                                        <div className={`text-[10px] font-semibold ${calcCost != null ? "text-amber-700 dark:text-amber-300" : "text-slate-400"}`}>
                                                                                                            Mlyt: {calcCost != null ? `${calcCost.toLocaleString("tr-TR")} ₺` : "—"}
                                                                                                        </div>
                                                                                                    )}
                                                                                                    <div className="text-[8px] text-slate-400 mt-0.5">× {mk || "—"}</div>
                                                                                                </div>
                                                                                            );
                                                                                        })}
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    if (mode === "flat") {
                                                                        return (
                                                                            <div className="flex items-end gap-2 flex-wrap">
                                                                                <div className="flex flex-col items-center gap-0.5">
                                                                                    <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Tek Fiyat</span>
                                                                                    {isNet && (
                                                                                        <input
                                                                                            type="number"
                                                                                            min="0"
                                                                                            value={row.flat_cost_price ?? ""}
                                                                                            onChange={(e) => updateBase(rt.id, "flat_cost_price", e.target.value)}
                                                                                            placeholder="Mlyt"
                                                                                            className="w-20 px-1 py-1 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] font-semibold text-center text-amber-800 dark:text-amber-200"
                                                                                            data-testid={`bulk-flat-cost-${rt.id}`}
                                                                                        />
                                                                                    )}
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        value={row.flat_price ?? ""}
                                                                                        onChange={(e) => updateBase(rt.id, "flat_price", e.target.value)}
                                                                                        placeholder={isNet ? "Satış" : "Fiyat"}
                                                                                        className="w-20 px-1 py-1 rounded bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-[11px] font-semibold text-center text-sky-800 dark:text-sky-200"
                                                                                        data-testid={`bulk-flat-${rt.id}`}
                                                                                    />
                                                                                </div>
                                                                                <span className="text-[10px] text-slate-500 italic">Tüm kapasiteler aynı fiyat.</span>
                                                                            </div>
                                                                        );
                                                                    }
                                                                    // manual
                                                                    return (
                                                                        <div className="flex flex-wrap gap-2">
                                                                            {Array.from({ length: cap }, (_, i) => i + 1).map((k) => (
                                                                                <div key={k} className="flex flex-col items-center gap-0.5">
                                                                                    <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">{k} kişi</span>
                                                                                    {isNet && (
                                                                                        <input
                                                                                            type="number"
                                                                                            min="0"
                                                                                            value={row.cost_prices?.[k] ?? ""}
                                                                                            onChange={(e) => updateRowCost(rt.id, k, e.target.value)}
                                                                                            placeholder="Mlyt"
                                                                                            className="w-16 px-1 py-1 rounded bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-[11px] font-semibold text-center text-amber-800 dark:text-amber-200"
                                                                                            data-testid={`bulk-cost-${rt.id}-${k}`}
                                                                                        />
                                                                                    )}
                                                                                    <input
                                                                                        type="number"
                                                                                        min="0"
                                                                                        value={row.prices?.[k] ?? ""}
                                                                                        onChange={(e) => updateRowPrice(rt.id, k, e.target.value)}
                                                                                        placeholder={isNet ? "Satış" : "Fiyat"}
                                                                                        className="w-16 px-1 py-1 rounded bg-sky-50 dark:bg-sky-950/30 border border-sky-200 dark:border-sky-900 text-[11px] font-semibold text-center text-sky-800 dark:text-sky-200"
                                                                                        data-testid={`bulk-price-${rt.id}-${k}`}
                                                                                    />
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    );
                                                                })()
                                                            ) : operation === "quota" ? (
                                                                <input
                                                                    type="number"
                                                                    min="0"
                                                                    value={row.quota ?? ""}
                                                                    onChange={(e) => updateRowField(rt.id, "quota", e.target.value)}
                                                                    placeholder="—"
                                                                    className="w-20 px-1.5 py-1 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] font-semibold text-center text-slate-900 dark:text-white"
                                                                    data-testid={`bulk-quota-${rt.id}`}
                                                                />
                                                            ) : operation === "stop_sale" ? (
                                                                <div className="flex flex-wrap gap-1.5">
                                                                    {[
                                                                        { f: "stop_sale", on: "Stop", off: "Satış Açık", idle: "Stop: —", tid: `bulk-stop-${rt.id}` },
                                                                        { f: "closed_to_arrival", on: "Girişe Kapalı", off: "Girişe Açık", idle: "Giriş: —", tid: `bulk-cta-${rt.id}` },
                                                                        { f: "closed_to_departure", on: "Çıkışa Kapalı", off: "Çıkışa Açık", idle: "Çıkış: —", tid: `bulk-ctd-${rt.id}` },
                                                                    ].map((t) => (
                                                                        <button
                                                                            key={t.f}
                                                                            type="button"
                                                                            onClick={() => updateRowField(rt.id, t.f, row[t.f] == null ? true : (row[t.f] ? false : null))}
                                                                            className={`h-7 px-2 rounded text-[10px] font-bold transition-colors ${row[t.f] === true ? "bg-red-500 text-white" : row[t.f] === false ? "bg-emerald-500 text-white" : "bg-slate-200 dark:bg-slate-700 text-slate-500"}`}
                                                                            data-testid={t.tid}
                                                                        >
                                                                            {row[t.f] === true ? t.on : row[t.f] === false ? t.off : t.idle}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            ) : operation === "profit_margin" ? (
                                                                <span className="text-[10px] text-emerald-700 dark:text-emerald-300 italic" data-testid={`bulk-margin-info-${rt.id}`}>
                                                                    Mevcut maliyetlere %{bulkMarginPct || 0} kar marjı uygulanacak
                                                                </span>
                                                            ) : operation === "release" ? (
                                                                <span className="text-[10px] text-purple-700 dark:text-purple-300 italic" data-testid={`bulk-release-info-${rt.id}`}>
                                                                    {Number(releaseDays) > 0
                                                                        ? `${releaseDays} gün release uygulanacak`
                                                                        : "Release kaldırılacak"}
                                                                </span>
                                                            ) : (
                                                                <div className="flex items-center gap-2">
                                                                    <div className="flex flex-col items-start gap-0.5">
                                                                        <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Min</span>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={row.min ?? ""}
                                                                            onChange={(e) => updateRowField(rt.id, "min", e.target.value)}
                                                                            placeholder="—"
                                                                            className="w-14 px-1.5 py-1 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-center text-slate-900 dark:text-white"
                                                                        />
                                                                    </div>
                                                                    <div className="flex flex-col items-start gap-0.5">
                                                                        <span className="text-[9px] uppercase tracking-wider font-bold text-slate-500">Max</span>
                                                                        <input
                                                                            type="number"
                                                                            min="1"
                                                                            value={row.max ?? ""}
                                                                            onChange={(e) => updateRowField(rt.id, "max", e.target.value)}
                                                                            placeholder="—"
                                                                            className="w-14 px-1.5 py-1 rounded bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-[11px] text-center text-slate-900 dark:text-white"
                                                                        />
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    <p className="px-2 py-1.5 bg-slate-50 dark:bg-slate-800/50 text-[10px] text-slate-500 italic border-t border-slate-200 dark:border-slate-700">Boş bırakılan alanlar değiştirilmez. Seçili olmayan odalar atlanır.</p>
                                </div>
                            )}
                        </Section>
                    )}

                    {/* Step 4: Date range + weekdays */}
                    {planId && (
                        <Section step="4" title="Tarih Aralığı">
                            <div className="grid grid-cols-2 gap-2 mb-2">
                                <Field label="Başlangıç">
                                    <input data-testid="bulk-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className={INPUT_SM} />
                                </Field>
                                <Field label="Bitiş">
                                    <input data-testid="bulk-end" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className={INPUT_SM} />
                                </Field>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {WEEKDAYS_LBL.map((w, i) => (
                                    <button
                                        key={w}
                                        type="button"
                                        onClick={() => toggleWd(i)}
                                        data-testid={`wd-${i}`}
                                        className={`px-3 py-1 rounded-full text-[11px] font-semibold transition-colors ${weekdays.includes(i) ? "bg-sky-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-700"}`}
                                    >{w}</button>
                                ))}
                            </div>
                        </Section>
                    )}

                    {msg && <div className={`p-2.5 rounded-xl text-[11px] ${msg.startsWith("✓") ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`}>{msg}</div>}
                </div>

                <div className="sticky bottom-0 px-5 py-3 border-t border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800">İptal</button>
                    <button data-testid="bulk-submit" onClick={submit} disabled={saving || !planId} className="px-6 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold disabled:opacity-60">
                        {saving ? "Uygulanıyor..." : "Uygula"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function Section({ step, title, children }) {
    return (
        <div>
            <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-sky-500 text-white text-[10px] font-bold">{step}</span>
                <span className="text-xs font-bold uppercase tracking-widest text-slate-700 dark:text-slate-200">{title}</span>
            </div>
            {children}
        </div>
    );
}

const INPUT_SM = "w-full px-2.5 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs font-medium text-slate-900 dark:text-white";

const INPUT = "w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-sm font-medium text-slate-900 dark:text-white";

function Field({ label, children, className = "" }) {
    return (
        <label className={`block ${className}`}>
            <span className="block text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</span>
            {children}
        </label>
    );
}


function SelectionBar({ selection, count, plans, bulkValue, setBulkValue, saving, onClear, onApply }) {
    const plan = plans.find((p) => p.id === selection.planId);
    const fieldLabels = {
        stop_sale: "Müsaitlik",
        quota: "Kontenjan",
        price: "Satış / Fiyat",
        cost_price: "Maliyet",
        price_1: "Fiyat 1 kişi",
        price_2: "Fiyat 2 kişi",
        price_3: "Fiyat 3 kişi",
        discount: "İndirim",
    };
    const isStop = selection.fieldType === "stop_sale";
    const isDiscount = selection.fieldType === "discount";
    const [discType, setDiscType] = React.useState("percent");
    const fmtDate = (s) => { const [y,m,d] = s.split("-"); return `${d}.${m}.${y}`; };

    return (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[min(820px,calc(100%-32px))] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border-2 border-sky-500 px-5 py-4" data-testid="selection-bar">
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex-1 min-w-[220px]">
                    <div className="text-xs font-bold uppercase tracking-widest text-sky-600">
                        {count} hücre seçildi · {fieldLabels[selection.fieldType] || selection.fieldType}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                        {fmtDate(selection.start)} → {fmtDate(selection.end)} · {plan ? `${plan.code} · ${plan.name}` : ""}
                    </div>
                </div>

                {isStop ? (
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => onApply("stop_sale", null)}
                            disabled={saving}
                            data-testid="sel-stop"
                            className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center gap-1.5"
                        ><X className="h-4 w-4" /> Satışı Kapat</button>
                        <button
                            onClick={() => onApply("open_sale", null)}
                            disabled={saving}
                            data-testid="sel-open"
                            className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold disabled:opacity-60 inline-flex items-center gap-1.5"
                        ><Check className="h-4 w-4" /> Satışı Aç</button>
                        {[
                            { op: "close_arrival", l: "Girişe Kapat", c: "bg-orange-500 hover:bg-orange-600", tid: "sel-close-arrival" },
                            { op: "open_arrival", l: "Girişe Aç", c: "bg-slate-600 hover:bg-slate-700", tid: "sel-open-arrival" },
                            { op: "close_departure", l: "Çıkışa Kapat", c: "bg-violet-500 hover:bg-violet-600", tid: "sel-close-departure" },
                            { op: "open_departure", l: "Çıkışa Aç", c: "bg-slate-600 hover:bg-slate-700", tid: "sel-open-departure" },
                        ].map((b) => (
                            <button key={b.op} onClick={() => onApply(b.op, null)} disabled={saving} data-testid={b.tid} className={`px-3 py-2 rounded-xl text-white text-xs font-bold disabled:opacity-60 ${b.c}`}>{b.l}</button>
                        ))}
                    </div>
                ) : isDiscount ? (
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex gap-1">
                            {[
                                { v: "percent", l: "%" },
                                { v: "fixed", l: "₺" },
                                { v: "override", l: "→₺" },
                            ].map((o) => (
                                <button key={o.v} onClick={() => setDiscType(o.v)}
                                    className={`px-2.5 py-2 rounded-lg text-xs font-bold ${discType === o.v ? "bg-amber-500 text-white" : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300"}`}>
                                    {o.l}
                                </button>
                            ))}
                        </div>
                        <input
                            type="number"
                            min="0"
                            value={bulkValue}
                            onChange={(e) => setBulkValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && bulkValue !== "") {
                                    onApply(`discount_${discType}`, bulkValue);
                                }
                            }}
                            placeholder={discType === "percent" ? "yüzde %" : discType === "fixed" ? "tutar (₺)" : "yeni fiyat (₺)"}
                            autoFocus
                            data-testid="sel-disc-input"
                            className="w-32 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 focus:border-amber-400 outline-none text-sm font-semibold text-slate-900 dark:text-white"
                        />
                        <button
                            onClick={() => onApply(`discount_${discType}`, bulkValue)}
                            disabled={saving || bulkValue === ""}
                            data-testid="sel-disc-apply"
                            className="px-4 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white text-sm font-bold disabled:opacity-60"
                        >{saving ? "..." : "Uygula"}</button>
                        <button
                            onClick={() => onApply("discount_clear", null)}
                            disabled={saving}
                            data-testid="sel-disc-clear"
                            className="px-3 py-2 rounded-xl bg-red-50 hover:bg-red-100 dark:bg-red-950/40 text-red-600 text-xs font-bold disabled:opacity-60"
                        >Kaldır</button>
                    </div>
                ) : (
                    <>
                        <input
                            type="number"
                            min="0"
                            value={bulkValue}
                            onChange={(e) => setBulkValue(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && bulkValue !== "") {
                                    onApply(selection.fieldType === "quota" ? "quota_only" : "price_only", bulkValue);
                                }
                            }}
                            placeholder={selection.fieldType === "quota" ? "kontenjan" : "fiyat (₺)"}
                            autoFocus
                            data-testid="sel-input"
                            className="w-32 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 focus:border-sky-400 outline-none text-sm font-semibold text-slate-900 dark:text-white"
                        />
                        <button
                            onClick={() => onApply(selection.fieldType === "quota" ? "quota_only" : "price_only", bulkValue)}
                            disabled={saving || bulkValue === ""}
                            data-testid="sel-apply"
                            className="px-5 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-sm font-bold disabled:opacity-60"
                        >{saving ? "..." : "Uygula"}</button>
                    </>
                )}

                <button onClick={onClear} data-testid="sel-clear" className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800" title="Seçimi temizle">
                    <X className="h-4 w-4 text-slate-500" />
                </button>
            </div>
        </div>
    );
}
